import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Node, Edge } from '@xyflow/react';
import { checkDfd, DfdViolation } from '../utils/dfdRules';
import { updateEdge, updateNodeData, setSelectedNode } from '../../../store/workflow/workflowSlice';
import { useTranslation } from 'react-i18next';
import FlowRulesDialog from './FlowRulesDialog';

interface Props { nodes: Node[]; edges: Edge[] }

/**
 * DFD 규칙 검사 바 — 위반이 있으면 캔버스 위에 건수와 목록을 보여 준다.
 * "예외 허용" 은 해당 엣지/노드에 dfdException 을 표시해 검사에서 제외한다(실무의 특수한 경우).
 *
 * 오른쪽 끝의 "연결 N건" 은 예전 의존성 바(모든 엣지를 `A >> B` 로 나열)를 대신한다. 그 바는 데이터
 * 노드까지 태스크처럼 보여 실제 DAG 와 달랐고, 정확한 의존성은 헤더의 "DAG 코드" 가 이미 보여 준다.
 */
const DfdCheckBar: React.FC<Props> = ({ nodes, edges }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const violations = useMemo(() => checkDfd(nodes, edges), [nodes, edges]);
  const exceptions = useMemo(() =>
    edges.filter((e) => (e.data as any)?.dfdException).length + nodes.filter((n) => (n.data as any)?.dfdException).length, [nodes, edges]);

  const allow = (v: DfdViolation) => {
    if (v.kind === 'edge') {
      v.edgeIds.forEach((eid) => {
        const e = edges.find((x) => x.id === eid);
        if (e) dispatch(updateEdge({ ...e, data: { ...(e.data || {}), dfdException: true } }));
      });
    } else {
      const n = nodes.find((x) => x.id === v.nodeIds[0]);
      if (n) dispatch(updateNodeData({ id: n.id, data: { dfdException: true } } as any));
    }
  };
  const clearExceptions = () => {
    edges.filter((e) => (e.data as any)?.dfdException).forEach((e) => dispatch(updateEdge({ ...e, data: { ...(e.data || {}), dfdException: false } })));
    nodes.filter((n) => (n.data as any)?.dfdException).forEach((n) => dispatch(updateNodeData({ id: n.id, data: { dfdException: false } } as any)));
  };

  if (nodes.length === 0) return null;
  const ok = violations.length === 0;
  return (
    <div className={`shrink-0 border-b border-gray-200 text-xs ${ok ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-800'}`}>
      <div className="flex items-center gap-2 px-2 py-1">
        <span className="font-semibold">{t('workflow:dfd.title')}</span>
        {ok ? (
          <span>{t('workflow:dfd.ok')}{exceptions ? t('workflow:dfd.okExceptions', { n: exceptions }) : ''}</span>
        ) : (
          <button type="button" className="underline" onClick={() => setOpen((o) => !o)}>
            {t('workflow:dfd.violations', { n: violations.length })} {open ? '▴' : '▾'}
          </button>
        )}
        {exceptions > 0 && (
          <button type="button" className="px-1.5 py-0.5 rounded border border-gray-300 hover:bg-gray-200 text-gray-600" onClick={clearExceptions} title={t('workflow:dfd.clearTitle')}>
            {t('workflow:dfd.clear', { n: exceptions })}
          </button>
        )}
        <button type="button" className="px-1.5 py-0.5 rounded border border-gray-300 hover:bg-gray-200 text-gray-600"
          onClick={() => setRulesOpen(true)} title={t('workflow:flowRule.openTitle')}>
          {t('workflow:flowRule.open')}
        </button>
        <span className="ml-auto text-gray-400" title={t('workflow:dfd.countsTitle')}>
          {t('workflow:dfd.counts', { nodes: nodes.length, edges: edges.length })}
        </span>
      </div>
      {!ok && open && (
        <ul className="px-2 pb-1.5 space-y-0.5 max-h-28 overflow-y-auto">
          {violations.map((v) => (
            <li key={v.id} className="flex items-center gap-2 pr-40">
              {/* 우상단 다운로드 버튼 오버레이와 겹치지 않도록 버튼을 왼쪽에 둔다 */}
              {/* 순환은 예외로 둘 수 없다 — 실행 순서를 정할 수 없어 배포·실행이 안 된다 */}
              {v.kind === 'cycle' ? (
                <span className="shrink-0 px-1.5 py-0.5 rounded border border-red-300 text-red-700" title={t('workflow:dfd.cycleTitle')}>{t('workflow:dfd.cycleTag')}</span>
              ) : (
                <button type="button" className="shrink-0 px-1.5 py-0.5 rounded border border-amber-300 hover:bg-amber-100" onClick={() => allow(v)}
                  title={t('workflow:dfd.allowTitle')}>{t('workflow:dfd.allow')}</button>
              )}
              <button type="button" className="text-left hover:underline flex-1 truncate" title={v.message}
                onClick={() => dispatch(setSelectedNode(v.nodeIds[0]))}>{v.message}</button>
            </li>
          ))}
        </ul>
      )}
      {rulesOpen && <FlowRulesDialog onClose={() => setRulesOpen(false)} />}
    </div>
  );
};

export default DfdCheckBar;
